import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Loader } from "lucide-react";
import Navbar from "../components/Navbar";
import NewsTicker from "../components/NewsTicker";
import HeroStrip from "../components/dashboard/HeroStrip";
import Collapsible from "../components/dashboard/Collapsible";
import { ResumeCard, ToolsCard, TailorCard, MakeoverCard, NetworkCard, InterviewCard, StonesCard, CareerToolsCollapsible } from "../components/dashboard/LeftCards";
import InsightsPanel from "../components/dashboard/InsightsPanel";
import ImpressPanel from "../components/dashboard/ImpressPanel";
import InterviewTestDrivePanel from "../components/dashboard/InterviewTestDrivePanel";
import SteppingStonesPanel from "../components/dashboard/SteppingStonesPanel";
import PhaseProgressBar from "../components/dashboard/PhaseProgressBar";
import NetworkPromoBanner from "../components/dashboard/NetworkPromoBanner";  // eslint-disable-line no-unused-vars
import DashboardSuperpowerCard from "../components/dashboard/DashboardSuperpowerCard";  // eslint-disable-line no-unused-vars
import DashboardSuperpowerPanel from "../components/dashboard/DashboardSuperpowerPanel";
import StrategyBriefCard from "../components/dashboard/StrategyBriefCard";
import IntegrityCard from "../components/dashboard/IntegrityCard";
import MultiResumeSummary from "../components/dashboard/MultiResumeSummary";
import InterviewCalendarCard from "../components/dashboard/InterviewCalendarCard";
import SchedulingCelebration from "../components/dashboard/SchedulingCelebration";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import { api } from "../lib/api";
import Welcome from "./Welcome";

export default function Landing() {
  const navigate = useNavigate();   // eslint-disable-line no-unused-vars
  const { user, refresh } = useAuth();
  const { mode, setMode } = useTheme();
  const isPro = mode === "promo";
  const firstName = (user?.name || "").split(" ")[0] || "";
  const [modeSaving, setModeSaving] = useState(false);
  const [data, setData] = useState(null);

  const pickMode = async (flavor) => {
    if (modeSaving) return;
    setModeSaving(true);
    try { await setMode(flavor); } finally { setModeSaving(false); }
  };

  // Trigger the HeroStrip's hidden file input (used by the left-side ResumeCard).
  const triggerUpload = () => {
    const input = document.querySelector('[data-testid="hero-replace-resume"], [data-testid="hero-add-resume"]');
    if (input) input.click();
  };

  // Fetch dashboard payload (insights + makeover preview + resume snippet).
  const fetchDashboard = async () => {
    try {
      const res = await api.get("/landing/dashboard");
      setData(res.data);
    } catch { /* non-fatal — empty states render */ }
  };

  useEffect(() => {
    if (!user) return;
    let alive = true;
    (async () => {
      try {
        const res = await api.get("/landing/dashboard");
        if (alive) setData(res.data);
      } catch { /* non-fatal — empty states render */ }
    })();
    return () => { alive = false; };
  }, [user]);

  // "Start here" next-phase pulse — once per progression milestone, gently
  // pulse the next-incomplete phase card so returning users always know
  // where to pick up. Mirrors the Welcome page first-tile suggestion.
  // We persist the last-pulsed phase id in localStorage so the cue only
  // fires when the user has actually progressed (not on every visit).
  useEffect(() => {
    if (!data) return undefined;
    const doneFlags = [
      !!data.has_resume,
      !!data.makeover,
      (data.network_contact_count || 0) > 0,
      !!data.makeover && (data.recent_playbooks || []).length > 0,
      (data.recent_stones || []).length > 0,
    ];
    const nextIdx = doneFlags.findIndex((d) => !d);
    if (nextIdx === -1) return undefined; // all five done — no nudge needed
    const phaseToTestid = ["resume-card", "tailor-card", "network-card", "interview-card", "stones-card"];
    const targetTestid = phaseToTestid[nextIdx];
    // Idempotency: only pulse if this is a NEW milestone since last visit.
    let lastPulsed = null;
    try { lastPulsed = window.localStorage.getItem("resmo.next-phase-pulsed"); } catch { /* localStorage blocked */ }
    if (lastPulsed === String(nextIdx)) return undefined;
    let pulseEl = null;
    let cancelled = false;
    const onCardInteract = () => {
      cancelled = true;
      if (pulseEl) pulseEl.classList.remove("rm-suggest-pulse");
    };
    // Give the user ~2.4s to take in the dashboard before nudging.
    const timer = setTimeout(() => {
      if (cancelled) return;
      pulseEl = document.querySelector(`[data-testid="${targetTestid}"]`);
      if (!pulseEl) return;
      pulseEl.classList.remove("rm-suggest-pulse");
      void pulseEl.offsetWidth;
      pulseEl.classList.add("rm-suggest-pulse");
      try { window.localStorage.setItem("resmo.next-phase-pulsed", String(nextIdx)); } catch { /* noop */ }
      // Any click inside the card cancels — user is already engaging.
      pulseEl.addEventListener("click", onCardInteract, { once: true });
    }, 2400);
    return () => {
      cancelled = true;
      clearTimeout(timer);
      if (pulseEl) {
        pulseEl.classList.remove("rm-suggest-pulse");
        pulseEl.removeEventListener("click", onCardInteract);
      }
    };
  }, [data]);

  const hasResume = !!data?.has_resume;

  // Logged-out visitors get the marketing Welcome page.
  if (user === false) return <Welcome />;

  // user === null means auth is still loading — show a spinner.
  if (!user) {
    return (
      <div className="min-h-screen rm-paper flex items-center justify-center">
        <Loader size={28} className="animate-spin rm-text-orange" />
      </div>
    );
  }

  return (
    <div className="min-h-screen rm-paper">
      <Navbar />
      <NewsTicker />
      <SchedulingCelebration />

      <div className="max-w-7xl mx-auto px-4 md:px-8 pt-5 md:pt-6 pb-8">
        <HeroStrip
          user={user}
          firstName={firstName}
          hasResume={hasResume}
          isPro={isPro}
          pickMode={pickMode}
          modeSaving={modeSaving}
          refresh={refresh}
        />

        <div className="mt-4 md:mt-5">
          <PhaseProgressBar
            doneFlags={[
              !!hasResume,
              !!data?.makeover,
              (data?.network_contact_count || 0) > 0,
              !!data?.makeover && (data?.recent_playbooks || []).length > 0,
              (data?.recent_stones || []).length > 0,
            ]}
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 md:gap-5 items-start mt-4 md:mt-5">
          {/* LEFT — 5 phase cards (Resume + Skill Assess, JD match, Studio,
              Interview, Stones) + Career Tools grid at the bottom */}
          <div className="lg:col-span-7 space-y-4" data-testid="left-stack">
            <div data-testid="ingredients-heading">
              <div className="rm-cursive" style={{ color: "#0F1B2D", fontSize: "2.5rem", lineHeight: 0.9 }}>
                The Ingredients
              </div>
              <div className="font-mono-rm text-[11px] uppercase tracking-widest"
                   style={{ color: "#FF6A00", fontWeight: 700 }}>
                · What you feed Resmo — resume, target role, job description
              </div>
            </div>
            <ResumeCard
              hasResume={hasResume}
              resumePreview={data?.resume_preview || ""}
              resumeFilename={data?.resume_filename || ""}
              resumeUpdatedAt={data?.resume_updated_at || null}
              onUploadClick={triggerUpload}
              hasAssessment={data?.has_assessment}
            />
            <TailorCard hasResume={hasResume} done={!!data?.makeover} doneAt={data?.makeover?.created_at} />
            <MakeoverCard makeover={data?.makeover} studioStatus={data?.studio_status} />
            <NetworkCard
              contactCount={data?.network_contact_count || 0}
              hasPitch={!!data?.network_has_pitch}
            />
            <InterviewCard makeover={data?.makeover} recentPlaybooks={data?.recent_playbooks || []} />
            <StonesCard recentStones={data?.recent_stones} />

            <div data-testid="utensils-heading" className="pt-4">
              <div className="rm-cursive" style={{ color: "#0F1B2D", fontSize: "2.5rem", lineHeight: 0.9 }}>
                The Utensils
              </div>
              <div className="font-mono-rm text-[11px] uppercase tracking-widest"
                   style={{ color: "#0E5A4A", fontWeight: 700 }}>
                · Resmo's career tools — grab one whenever you need it
              </div>
            </div>
            <CareerToolsCollapsible hasResume={hasResume} defaultOpen={true} />
          </div>

          {/* RIGHT — "The Meal" — outputs Resmo discovered for the user */}
          <div className="lg:col-span-5 space-y-4 md:space-y-5">
            <div data-testid="meal-heading">
              <div className="rm-cursive" style={{ color: "#0F1B2D", fontSize: "2.5rem", lineHeight: 0.9 }}>
                The Meal
              </div>
              <div className="font-mono-rm text-[11px] uppercase tracking-widest"
                   style={{ color: "#1E40AF", fontWeight: 700 }}>
                · What Resmo discovered about you and produced for you
              </div>
            </div>
            <StrategyBriefCard makeover={data?.makeover} />
            <IntegrityCard makeover={data?.makeover} />
            <InterviewCalendarCard />
            <MultiResumeSummary />
            <Collapsible
              testid="superpower-collapsible"
              title="Your Superpower"
              eyebrow="The thing recruiters remember 30 seconds later"
              accent="#0F1B2D"
              headerBg="linear-gradient(135deg, #FFC145 0%, #FF6A00 100%)"
              eyebrowColor="#0F1B2D"
              titleColor="#0F1B2D"
              defaultOpen={false}
            >
              <DashboardSuperpowerPanel />
            </Collapsible>
            <Collapsible
              testid="insights-collapsible"
              title="Personalized Insights"
              eyebrow="What your skills are worth in today's market"
              accent="#0F1B2D"
              headerBg="linear-gradient(135deg, #FBBF24 0%, #CA8A04 100%)"
              eyebrowColor="#0F1B2D"
              titleColor="#0F1B2D"
              defaultOpen={false}
            >
              <InsightsPanel
                hasResume={hasResume}
                skillsWorth={data?.skills_worth}
                topJobs={data?.top_jobs || []}
                adjacentRoles={data?.adjacent_roles || []}
                roleHint={data?.role_hint}
              />
            </Collapsible>
            <Collapsible
              testid="makeover-collapsible"
              title="Resume Makeover"
              eyebrow={data?.makeover ? `Latest tailor · ${data.makeover.job_title || ""}` : "Got a Job Description?"}
              accent="#0F1B2D"
              headerBg="linear-gradient(135deg, #F97316 0%, #B91C1C 100%)"
              eyebrowColor="#0F1B2D"
              titleColor="#0F1B2D"
              defaultOpen={false}
            >
              <ImpressPanel
                hasResume={hasResume}
                makeover={data?.makeover}
                fallbackFitScore={data?.fallback_fit_score}
                fallbackFitSource={data?.fallback_fit_source}
                studioStatus={data?.studio_status}
              />
            </Collapsible>
            <Collapsible
              testid="interview-collapsible"
              title="Interview Test Drive"
              eyebrow={data?.makeover ? "Pre-loaded with your latest Job Description" : "Locked · tailor a Job Description first"}
              accent="#0F1B2D"
              headerBg="linear-gradient(135deg, #3B82F6 0%, #1E40AF 100%)"
              eyebrowColor="#0F1B2D"
              titleColor="#0F1B2D"
              defaultOpen={false}
            >
              <InterviewTestDrivePanel
                recentPlaybooks={data?.recent_playbooks || []}
                makeover={data?.makeover}
              />
            </Collapsible>
            <Collapsible
              testid="stones-collapsible"
              title="Stepping Stones"
              eyebrow={(data?.recent_stones || []).length
                ? `${(data.recent_stones || []).length} reflection${(data.recent_stones || []).length > 1 ? "s" : ""} logged`
                : "After the interview — capture lessons"}
              accent="#0F1B2D"
              headerBg="linear-gradient(135deg, #10B981 0%, #047857 100%)"
              eyebrowColor="#0F1B2D"
              titleColor="#0F1B2D"
              defaultOpen={false}
            >
              <SteppingStonesPanel recentStones={data?.recent_stones || []} />
            </Collapsible>
          </div>
        </div>
      </div>

      <footer className="max-w-7xl mx-auto px-4 md:px-8 py-4 text-center" data-testid="landing-footer">
        <div className="font-mono-rm text-[10px] uppercase tracking-widest" style={{ color: "#94A3B8" }}>
          Resmo · Your career intelligence companion · © {new Date().getFullYear()}
        </div>
        <div className="font-mono-rm text-[10px] uppercase tracking-widest mt-1.5">
          <Link
            to="/employer/signup"
            className="inline-flex items-center gap-1"
            style={{ color: "#FF6A00", fontWeight: 700, textDecoration: "none", letterSpacing: "0.08em" }}
            data-testid="landing-footer-employer-link"
          >
            · For employers · Talent Directory →
          </Link>
        </div>
      </footer>
    </div>
  );
}
